/** @jsxImportSource @emotion/react */

import { useCallback, useEffect, useState } from "react";
import { css } from "@emotion/react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography
} from "@mui/material";
import BaseButton from "components/common/Button/Base";
import {
  getInvoiceSumCheck,
  InvoiceSumCheckErrorStore,
  InvoiceSumCheckInvoiceType,
  InvoiceSumCheckResult
} from "pages/remotes";
import { PALETTE } from "styles/base/variables";
import { convertCurrency } from "utils/converter";
import { bizNumberPipe } from "pipes/business.pipe";

const DEFAULT_SUM_CHECK_ERROR = "합계표 검증 중 오류가 발생했습니다.";

function getInvoiceSumCheckErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { message?: unknown } } })
    ?.response?.data;
  const msg = data?.message;
  if (typeof msg === "string" && msg.trim()) return msg.trim();
  return DEFAULT_SUM_CHECK_ERROR;
}

function parseAmount(v: number | string | undefined | null): number {
  if (v === null || v === undefined) return NaN;
  if (typeof v === "number") return v;
  const n = Number(String(v).replace(/,/g, ""));
  return n;
}

function formatAmount(v: number | string | undefined | null) {
  const n = parseAmount(v);
  if (Number.isNaN(n)) return "-";
  return convertCurrency(n);
}

interface InvoiceSumCheckDialogProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  invoiceType: InvoiceSumCheckInvoiceType;
  date: string;
}

function InvoiceSumCheckDialog({
  isOpen,
  onClose,
  businessId,
  invoiceType,
  date
}: InvoiceSumCheckDialogProps) {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [payload, setPayload] = useState<InvoiceSumCheckResult | null>(null);

  const resetDialogData = useCallback(() => {
    setPayload(null);
    setLoadError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!isOpen || !businessId || !date) return;

    setLoading(true);
    setLoadError(null);
    setPayload(null);

    let cancelled = false;

    getInvoiceSumCheck(businessId, { invoiceType, date })
      .then((res: any) => {
        if (cancelled) return;
        const data = (res?.data ?? res) as InvoiceSumCheckResult;
        setPayload(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(getInvoiceSumCheckErrorMessage(err));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, businessId, invoiceType, date]);

  const rows: InvoiceSumCheckErrorStore[] = payload?.errorStoreInfos || [];
  const dbSum = payload ? parseAmount(payload.dbSum) : NaN;
  const hometaxSum = payload ? parseAmount(payload.hometaxSum) : NaN;
  const sumsMatch =
    payload &&
    !Number.isNaN(dbSum) &&
    !Number.isNaN(hometaxSum) &&
    dbSum === hometaxSum;
  const isAligned =
    payload && !payload.isError && sumsMatch && rows.length === 0;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionProps={{ onExited: resetDialogData }}
    >
      <DialogTitle>합계표 검증</DialogTitle>
      <DialogContent>
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={120}
          >
            <CircularProgress size={36} sx={{ color: PALETTE.primary }} />
          </Box>
        )}
        {!loading && loadError && (
          <Typography color={PALETTE.error}>{loadError}</Typography>
        )}
        {!loading && !loadError && payload && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 12px 24px;
                font-size: 15px;
              `}
            >
              <Typography component="span" fontWeight={600}>
                검증 결과
              </Typography>
              <Typography
                component="span"
                fontWeight={700}
                color={isAligned ? PALETTE.progressCompleted : PALETTE.error}
              >
                {isAligned ? "일치" : "불일치"}
              </Typography>
              <Typography
                component="span"
                color={PALETTE.textSub}
                fontWeight={600}
              >
                세특 합계 {formatAmount(payload.dbSum)} / 홈택스 합계{" "}
                {formatAmount(payload.hometaxSum)}
              </Typography>
            </div>
            {rows.length > 0 && (
              <div
                css={css`
                  max-height: min(500px, calc(100dvh - 280px));
                  overflow: auto;
                  border: 1px solid ${PALETTE.border};
                  border-radius: 8px;
                `}
              >
                <table
                  css={css`
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 14px;
                  `}
                >
                  <thead>
                    <tr
                      css={css`
                        background-color: ${PALETTE.background};
                        text-align: left;
                      `}
                    >
                      <th css={thCss}>상호</th>
                      <th css={thCss}>사업자번호</th>
                      <th css={thCss}>거래 건수(세특)</th>
                      <th css={thCss}>거래 건수(홈택스)</th>
                      <th css={thCss}>거래액 합계(세특)</th>
                      <th css={thCss}>거래액 합계(홈택스)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr
                        key={`${row.bizNo}-${idx}`}
                        css={css`
                          border-top: 1px solid ${PALETTE.borderLight};
                        `}
                      >
                        <td css={tdCss}>{row.bizName}</td>
                        <td css={tdCss}>{bizNumberPipe(row.bizNo)}</td>
                        <td css={tdRight}>
                          {row.dbCount?.toLocaleString("ko-KR")}
                        </td>
                        <td css={tdRight}>
                          {row.hometaxCount?.toLocaleString("ko-KR")}
                        </td>
                        <td css={tdRight}>{formatAmount(row.dbSum)}</td>
                        <td css={tdRight}>{formatAmount(row.hometaxSum)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        <Divider sx={{ marginTop: "20px" }} />
      </DialogContent>
      <DialogActions sx={{ padding: "0 24px 20px 0" }}>
        <BaseButton
          variant="outlined"
          color={PALETTE.primary}
          onClick={onClose}
        >
          닫기
        </BaseButton>
      </DialogActions>
    </Dialog>
  );
}

const thCss = css`
  padding: 10px 12px;
  font-weight: 600;
  color: ${PALETTE.text};
  white-space: nowrap;
`;

const tdCss = css`
  padding: 10px 12px;
  color: ${PALETTE.text};
  vertical-align: middle;
`;

const tdRight = css`
  ${tdCss}
  text-align: right;
  white-space: nowrap;
`;

export default InvoiceSumCheckDialog;
